import { Slot as SlotPrimitive } from 'radix-ui'
import { cva } from 'class-variance-authority'
import { cn } from '@/utils/ui'
import Icon from '@mdi/react'
import { LoadingIndicator } from '@/components/ui/loadingIndicator'
import { useCallback, useState } from 'react'

export const buttonVariants = cva(
  // eslint-disable-next-line quotes
  "cursor-pointer inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-full font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        icon: 'size-9',
        lg: 'h-10 px-6 has-[>svg]:px-4',
        sm: 'h-8 px-3 has-[>svg]:px-2.5',
      },
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        destructiveGhost:
          'hover:bg-destructive text-destructive hover:text-white dark:hover:bg-destructive/50',
        destructiveOutline:
          'border bg-background shadow-xs hover:bg-destructive hover:text-destructive-foreground dark:bg-destructive/30 dark:border-destructive dark:hover:bg-destructive/50',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        outlinePrimary:
          'border border-primary bg-primary/70 shadow-xs hover:bg-primary dark:hover:bg-primary/50 text-primary-foreground',
        outlineSecondary:
          'border border-secondary bg-secondary/70 shadow-xs hover:bg-secondary dark:hover:bg-secondary/50 text-secondary-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        simple: 'bg-simple text-simple-foreground shadow-xs hover:bg-simple/90',
        simpleLink: 'text-simple underline-offset-4 hover:underline',
      },
    },
  }
)

export const Button = ({
  asChild = false,
  children,
  className,
  disabled = false,
  iconPath = '',
  iconSize = 1,
  loading = false,
  size,
  variant,
  onClick,
  ...props
}) => {
  const Comp = asChild ? SlotPrimitive.Slot : 'button'
  const [isAutoLoading, setIsAutoLoading] = useState(false)

  const isLoading = loading || isAutoLoading

  const handleClick = useCallback(
    async event => {
      if (!onClick || isLoading) return

      setIsAutoLoading(true)

      try {
        const result = onClick(event)
        if (result && typeof result.then === 'function') await result
      } finally {
        setIsAutoLoading(false)
      }
    },
    [isLoading, onClick]
  )

  return (
    <Comp
      className={cn(buttonVariants({ className, size, variant }))}
      data-slot="button"
      disabled={disabled || isLoading}
      onClick={handleClick}
      type="button"
      {...props}>
      {iconPath ? (
        <>
          {isLoading ? (
            <LoadingIndicator size={iconSize} />
          ) : (
            <Icon path={iconPath} size={iconSize} />
          )}

          {children && <span>{children}</span>}
        </>
      ) : isLoading ? (
        <LoadingIndicator size={iconSize} />
      ) : (
        children
      )}
    </Comp>
  )
}

export const GlowButton = ({
  className,
  iconPath = '',
  iconSize = 1,
  size,
  asChild = false,
  children,
  buttonProps = {},
  spanProps = {},
  ...props
}) => (
  <div
    className={cn(
      'relative inline-flex max-w-max overflow-hidden rounded-full px-[3px] py-[1px]',
      className
    )}
    {...props}>
    <span
      {...spanProps}
      className={cn(
        'from-promeet-blue via-promeet-magenta to-promeet-blue pointer-events-none absolute inset-[-1000%] animate-[spin_3s_linear_infinite] bg-conic/oklch',
        spanProps.className
      )}
    />

    <Button
      {...buttonProps}
      asChild={asChild}
      className={cn(
        'z-0 w-full translate-z-0 border-2 border-white/60 bg-white/40 text-black backdrop-blur-3xl backface-hidden hover:bg-white/60 dark:border-black/80 dark:bg-black/60 dark:text-white dark:hover:bg-black/80',
        buttonProps.className
      )}
      iconPath={iconPath}
      iconSize={iconSize}
      size={size}>
      {children}
    </Button>
  </div>
)
